import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, Sparkles, Zap, Video, Facebook, Instagram, Linkedin, Youtube, Twitter,
  CheckCircle, FileEdit, X, Loader2, TrendingUp, Lightbulb, Edit2, Database, Music,
  Target, Edit, Check, Plus, Search, MessageSquare, Copy, Download, RefreshCw,
  Send, Calendar, Clock, Share2
} from "lucide-react";
import PlatformIcon from '../components/PlatformIcon';
import { TIKTOK_VIDEO_ENABLED } from '../config/features';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const platformColors = {
  facebook: 'bg-blue-600 text-white',
  instagram: 'bg-gradient-to-r from-purple-500 to-pink-500 text-white',
  linkedin: 'bg-sky-700 text-white',
  tiktok: 'bg-gray-900 text-white',
  google_business: 'bg-orange-50 text-orange-600',
  youtube: 'bg-red-600 text-white',
  twitter: 'bg-sky-500 text-white',
};

const ContentGenerator = () => {
  const [prompt, setPrompt] = useState('');
  const [topic, setTopic] = useState('Bewerbungscoaching');
  const [platform, setPlatform] = useState('facebook');
  const [tone, setTone] = useState('professionell');
  const [model, setModel] = useState('claude');
  const [generatedContent, setGeneratedContent] = useState('');
  const [loading, setLoading] = useState(false);

  const topics = [
    'Bewerbungscoaching',
    'Existenzgründungscoaching',
    'AVGS-Förderung',
    'Karriereberatung',
    'Lebenslauf-Tipps',
    'Vorstellungsgespräch'
  ];

  const platforms = [
    { id: 'facebook', label: 'Facebook', icon: Facebook },
    { id: 'instagram', label: 'Instagram', icon: Instagram },
    { id: 'linkedin', label: 'LinkedIn', icon: Linkedin },
    { id: 'tiktok', label: 'TikTok', icon: null },
    { id: 'youtube', label: 'YouTube', icon: Youtube },
    { id: 'twitter', label: 'X/Twitter', icon: Twitter },
    { id: 'google_business', label: 'Google Business', icon: null }
  ].filter(p => TIKTOK_VIDEO_ENABLED || p.id !== 'tiktok');  // TikTok-Upload in Prüfung → ausgeblendet

  const tones = ['professionell', 'freundlich', 'motivierend', 'informativ'];

  const handleGenerate = async (useQuickDraft = false) => {
    if (!prompt.trim()) {
      toast.error('Bitte gib einen Prompt ein');
      return;
    }
    
    setLoading(true);
    try {
      const endpoint = useQuickDraft ? '/generate/quick-draft' : '/generate/text';
      const response = await axios.post(`${API}${endpoint}`, {
        prompt,
        topic,
        platform,
        tone,
        model: useQuickDraft ? 'groq' : model
      });
      setGeneratedContent(response.data.generated_text);
      toast.success(`Content mit ${response.data.model_used} generiert!`);
    } catch (error) {
      toast.error('Fehler bei der Generierung: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveContent = async () => {
    if (!generatedContent) return;
    
    try {
      await axios.post(`${API}/content`, {
        title: `${topic} - ${platform}`,
        content_type: 'text',
        text_content: generatedContent,
        platforms: [platform],
        status: 'draft'
      });
      toast.success('Content als Entwurf gespeichert!');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  return (
    <div data-testid="generator-page" className="space-y-8 animate-fade-in">
      <div>
        <h1 className="font-heading text-3xl font-bold text-stone-900">Content Generator</h1>
        <p className="text-stone-500 mt-1">Erstelle KI-generierte Social Media Inhalte</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Input Panel */}
        <div className="bg-white rounded-xl border border-stone-200 p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Thema</label>
            <select
              data-testid="topic-select"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              className="w-full p-3 border border-stone-200 rounded-lg focus:border-fox-fire focus:ring-2 focus:ring-fox-fire/20"
            >
              {topics.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Plattform</label>
            <div className="grid grid-cols-4 gap-2">
              {platforms.map(p => (
                <button
                  key={p.id}
                  data-testid={`platform-${p.id}`}
                  onClick={() => setPlatform(p.id)}
                  className={`p-3 rounded-lg border text-center transition-all ${
                    platform === p.id 
                      ? 'border-fox-fire bg-fox-fire/5 text-fox-fire' 
                      : 'border-stone-200 hover:border-stone-300'
                  }`}
                >
                  <span className="text-xs font-medium">{p.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Tonalität</label>
            <div className="flex flex-wrap gap-2">
              {tones.map(t => (
                <button
                  key={t}
                  onClick={() => setTone(t)}
                  className={`px-4 py-2 rounded-full text-sm transition-all ${
                    tone === t 
                      ? 'bg-fox-fire text-white' 
                      : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">KI-Modell</label>
            <div className="flex gap-3">
              <button
                data-testid="model-claude"
                onClick={() => setModel('claude')}
                className={`flex-1 p-4 rounded-lg border transition-all ${
                  model === 'claude' 
                    ? 'border-fox-fire bg-fox-fire/5' 
                    : 'border-stone-200 hover:border-stone-300'
                }`}
              >
                <Sparkles className={`w-5 h-5 mx-auto mb-1 ${model === 'claude' ? 'text-fox-fire' : 'text-stone-400'}`} />
                <span className="text-sm font-medium">Claude</span>
                <p className="text-xs text-stone-500">Beste Qualität</p>
              </button>
              <button
                data-testid="model-groq"
                onClick={() => setModel('groq')}
                className={`flex-1 p-4 rounded-lg border transition-all ${
                  model === 'groq' 
                    ? 'border-fox-fire bg-fox-fire/5' 
                    : 'border-stone-200 hover:border-stone-300'
                }`}
              >
                <Zap className={`w-5 h-5 mx-auto mb-1 ${model === 'groq' ? 'text-fox-fire' : 'text-stone-400'}`} />
                <span className="text-sm font-medium">Groq</span>
                <p className="text-xs text-stone-500">Schneller</p>
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Dein Prompt</label>
            <textarea
              data-testid="prompt-input"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="z.B. Erstelle einen motivierenden Post über die Vorteile von professionellem Bewerbungscoaching..."
              className="w-full p-4 border border-stone-200 rounded-lg h-32 resize-none focus:border-fox-fire focus:ring-2 focus:ring-fox-fire/20"
            />
          </div>

          <div className="flex gap-3">
            <button
              data-testid="generate-btn"
              onClick={() => handleGenerate(false)}
              disabled={loading}
              className="flex-1 btn-primary flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles size={18} />}
              {loading ? 'Generiere...' : 'Mit Claude generieren'}
            </button>
            <button
              data-testid="quick-draft-btn"
              onClick={() => handleGenerate(true)}
              disabled={loading}
              className="btn-secondary flex items-center gap-2"
            >
              <Zap size={18} />
              Quick Draft
            </button>
          </div>
        </div>

        {/* Output Panel */}
        <div className="bg-white rounded-xl border border-stone-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-bold">Generierter Content</h2>
            {generatedContent && (
              <div className="flex gap-2">
                <button
                  data-testid="save-content-btn"
                  onClick={handleSaveContent}
                  className="btn-secondary text-sm flex items-center gap-1"
                >
                  <FileEdit size={14} />
                  Speichern
                </button>
              </div>
            )}
          </div>
          
          {generatedContent ? (
            <div className="prose prose-stone max-w-none">
              <div className="p-4 bg-stone-50 rounded-lg whitespace-pre-wrap">
                {generatedContent}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-stone-400">
              <Sparkles className="w-12 h-12 mb-3 opacity-50" />
              <p>Dein generierter Content erscheint hier</p>
            </div>
          )}
        </div>
      </div>

      {/* Batch Generator */}
      <BatchGenerator />

      {/* Video-Generator entfernt: rief das Legacy-/api/generate/video, dessen
          Provider (Replicate) laut server.py gar nicht installiert ist — ein
          Versprechen, das nie eingelöst wurde. Der echte Video-Weg ist /video
          (Inhaber-Werkzeug). */}
    </div>
  );
};

// Batch Generator Component
const BatchGenerator = () => {
  const [days, setDays] = useState(7);
  const [postsPerDay, setPostsPerDay] = useState(2);
  const [selectedPlatforms, setSelectedPlatforms] = useState(['facebook', 'instagram', 'linkedin']);
  const [selectedTopics, setSelectedTopics] = useState(['Bewerbungscoaching', 'Existenzgründungscoaching', 'AVGS-Förderung']);
  const [tone, setTone] = useState('motivierend');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  // NEU: Content-Tools Integration
  const [targetAudience, setTargetAudience] = useState('');
  const [batchHook, setBatchHook] = useState('');
  const [hookLoading, setHookLoading] = useState(false);
  const [usePainPointTopics, setUsePainPointTopics] = useState(false);
  const [painPointIdeas, setPainPointIdeas] = useState([]);
  const [painPointLoading, setPainPointLoading] = useState(false);
  const [audiences, setAudiences] = useState([]);
  
  // NEU: Datenbasis Integration
  const [useDatenbasis, setUseDatenbasis] = useState(false);
  const [datenbasisProfile, setDatenbasisProfile] = useState(null);
  const [datenbasisLoading, setDatenbasisLoading] = useState(false);
  
  // NEU: Auda-Style Features
  const [includeTikTok, setIncludeTikTok] = useState(false);
  const [includeReviewLinks, setIncludeReviewLinks] = useState(true);
  // Default false: die Video-Integration ist kein Kundenprodukt mehr (Checkbox entfernt).
  const [includeVideo20Percent, setIncludeVideo20Percent] = useState(false);
  
  // PRO-FEATURE: Fertige Post-Entwürfe
  const [showProFeature, setShowProFeature] = useState(false);
  const [draftPosts, setDraftPosts] = useState([]);
  const [draftLoading, setDraftLoading] = useState(false);
  const [editingDraft, setEditingDraft] = useState(null);
  
  // Lade verfügbare Zielgruppen beim ersten Öffnen des Modals
  useEffect(() => {
    if (showModal && audiences.length === 0) {
      axios.get(`${API}/content-tools/pain-point/audiences`)
        .then(res => setAudiences(res.data.audiences || []))
        .catch(() => { /* Zielgruppen nicht kritisch – kein Toast nötig */ });
    }
  // audiences.length absichtlich nicht als Dependency – nur beim ersten Öffnen laden
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);
  
  // NEU: Datenbasis-Profil laden
  const loadDatenbasisProfile = async () => {
    setDatenbasisLoading(true);
    try {
      const response = await axios.get(`${API}/datenbasis/profile`);
      const profile = response.data.profile;
      setDatenbasisProfile(profile);
      setUseDatenbasis(true);
      toast.success('Datenbasis-Profil geladen!');
    } catch (error) {
      toast.error('Fehler beim Laden des Datenbasis-Profils');
    } finally {
      setDatenbasisLoading(false);
    }
  };

  const allPlatforms = ['facebook', 'instagram', 'linkedin', 'tiktok', 'youtube', 'twitter', 'google_business']
    .filter(p => TIKTOK_VIDEO_ENABLED || p !== 'tiktok');  // TikTok-Upload in Prüfung → ausgeblendet
  const allTopics = ['Bewerbungscoaching', 'Existenzgründungscoaching', 'AVGS-Förderung', 'Karriereberatung'];
  const tones = ['professionell', 'freundlich', 'motivierend', 'informativ'];

  const togglePlatform = (platform) => {
    setSelectedPlatforms(prev => 
      prev.includes(platform) 
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  const toggleTopic = (topic) => {
    setSelectedTopics(prev =>
      prev.includes(topic)
        ? prev.filter(t => t !== topic)
        : [...prev, topic]
    );
  };
  
  // NEU: Auto-Hook generieren
  const handleGenerateHook = async () => {
    if (!targetAudience) {
      toast.error('Bitte wähle erst eine Zielgruppe');
      return;
    }
    
    setHookLoading(true);
    try {
      const response = await axios.post(`${API}/content-tools/hook/generate`, {
        topic: selectedTopics[0] || 'Coaching',
        target_audience: targetAudience,
        channel: selectedPlatforms[0] || 'instagram'
      });
      setBatchHook(response.data.hook);
      toast.success('Batch-Hook generiert!');
    } catch (error) {
      toast.error('Fehler beim Hook generieren');
    } finally {
      setHookLoading(false);
    }
  };
  
  // NEU: Pain-Point-Ideen als Themen laden
  const handleLoadPainPointTopics = async () => {
    if (!targetAudience) {
      toast.error('Bitte wähle erst eine Zielgruppe');
      return;
    }
    
    setPainPointLoading(true);
    try {
      const response = await axios.post(`${API}/content-tools/pain-point/analyze`, {
        target_audience: targetAudience,
        offering: selectedTopics[0] || 'Coaching'
      });
      
      const ideas = response.data.content_ideas || [];
      setPainPointIdeas(ideas);
      
      // Extrahiere Themen aus den Ideen
      if (ideas.length > 0) {
        const newTopics = ideas.slice(0, 5).map(idea => idea.pain_point.substring(0, 50));
        setSelectedTopics(prev => [...new Set([...prev, ...newTopics])].slice(0, 6));
        setUsePainPointTopics(true);
        toast.success(`${ideas.length} Content-Ideen geladen!`);
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Pain-Point-Ideen');
    } finally {
      setPainPointLoading(false);
    }
  };
  
  // PRO-FEATURE: Fertige Posts aus Pain-Points generieren
  const handleGenerateFullPosts = async () => {
    if (!targetAudience) {
      toast.error('Bitte wähle erst eine Zielgruppe');
      return;
    }
    
    setDraftLoading(true);
    try {
      const response = await axios.post(`${API}/content-tools/pain-point/generate-posts`, {
        target_audience: targetAudience,
        offering: selectedTopics[0] || 'Bewerbungscoaching',
        channel: selectedPlatforms[0] || 'instagram',
        count: 3
      });
      
      setDraftPosts(response.data.posts || []);
      setShowProFeature(true);
      toast.success(`${response.data.count} fertige Post-Entwürfe generiert!`);
    } catch (error) {
      toast.error('Fehler beim Generieren der Posts');
    } finally {
      setDraftLoading(false);
    }
  };
  
  // Draft-Post bearbeiten
  const updateDraft = (index, field, value) => {
    setDraftPosts(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };
  
  // Draft löschen
  const removeDraft = (index) => {
    setDraftPosts(prev => prev.filter((_, i) => i !== index));
    if (draftPosts.length <= 1) {
      setShowProFeature(false);
    }
  };

  const totalPosts = days * postsPerDay * selectedPlatforms.length;
  const estimatedTime = Math.ceil(totalPosts * 2 / 60); // minutes

  const handleGenerate = async () => {
    if (selectedPlatforms.length === 0) {
      toast.error('Wähle mindestens eine Plattform');
      return;
    }
    if (selectedTopics.length === 0) {
      toast.error('Wähle mindestens ein Thema');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      // Wenn Auda-Style Features aktiviert, nutze den speziellen Auda-Endpoint
      const useAudaFeatures = includeTikTok || includeReviewLinks || includeVideo20Percent;
      
      if (useAudaFeatures) {
        // Auda-Enhanced Batch über OpenClaw Agent
        const audaResponse = await axios.post(`${API}/openclaw/auda/batch-enhanced`, {
          topic: selectedTopics[0] || 'Bewerbungscoaching',
          num_posts: days * postsPerDay,
          include_tiktok: includeTikTok,
          include_reviews: includeReviewLinks,
          include_video_20_percent: includeVideo20Percent
        });
        
        // Kombiniertes Result erstellen
        setResult({
          ...audaResponse.data.batch,
          days,
          platforms: selectedPlatforms,
          total_generated: audaResponse.data.batch.total_posts,
          auda_stats: audaResponse.data.auda_stats,
          content_tools_integration: {
            tools_used: [
              ...(includeTikTok ? ['TikTok-Reels'] : []),
              ...(includeReviewLinks ? ['Google Reviews Booster'] : []),
              ...(includeVideo20Percent ? ['LongCat Video'] : [])
            ],
            auda_enabled: true
          }
        });
        
        const statsMsg = [];
        if (audaResponse.data.auda_stats?.posts_with_tiktok > 0) {
          statsMsg.push(`${audaResponse.data.auda_stats.posts_with_tiktok} TikTok-Reels`);
        }
        if (audaResponse.data.auda_stats?.review_links_added) {
          statsMsg.push('Review-Links hinzugefügt');
        }
        if (audaResponse.data.auda_stats?.posts_with_video > 0) {
          statsMsg.push(`${audaResponse.data.auda_stats.posts_with_video} Videos`);
        }
        
        toast.success(`${audaResponse.data.batch.total_posts} Posts mit Auda-Features generiert! ${statsMsg.join(', ')}`);
      } else {
        // Standard Batch Generator
        const response = await axios.post(`${API}/generate/batch`, {
          topics: selectedTopics,
          platforms: selectedPlatforms,
          tone,
          days,
          posts_per_day: postsPerDay,
          // NEU: Content-Tools Daten mitsenden
          target_audience: targetAudience || null,
          batch_hook: batchHook || null,
          use_pain_point_topics: usePainPointTopics,
          // NEU: Datenbasis-Integration
          use_datenbasis: useDatenbasis,
          datenbasis_profile: useDatenbasis ? datenbasisProfile : null
        });
        setResult(response.data);
        toast.success(`${response.data.total_generated} Posts erfolgreich generiert!`);
      }
      
      setShowModal(false);
    } catch (error) {
      toast.error('Fehler: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="bg-gradient-to-br from-fox-fire/10 via-orange-50 to-amber-50 rounded-xl border border-orange-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-fox-fire rounded-xl flex items-center justify-center">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-bold text-stone-900">Batch Generator</h2>
              <p className="text-sm text-stone-600">Generiere eine ganze Woche Content mit einem Klick</p>
            </div>
          </div>
          <button
            data-testid="open-batch-modal-btn"
            onClick={() => setShowModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Sparkles size={18} />
            Batch starten
          </button>
        </div>

        {result && (
          <div className="mt-6 p-4 bg-white rounded-lg border border-orange-200">
            <div className="flex items-center gap-2 text-green-600 mb-3">
              <CheckCircle size={20} />
              <span className="font-semibold">{result.total_generated} Posts generiert!</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-stone-500">Tage:</span>
                <span className="font-medium ml-2">{result.days}</span>
              </div>
              <div>
                <span className="text-stone-500">Plattformen:</span>
                <span className="font-medium ml-2">{result.platforms?.length}</span>
              </div>
              <div>
                <span className="text-stone-500">Erfolgreich:</span>
                <span className="font-medium ml-2 text-green-600">{result.total_generated}</span>
              </div>
              <div>
                <span className="text-stone-500">Status:</span>
                <span className="font-medium ml-2">Eingeplant</span>
              </div>
            </div>
            {/* NEU: Content-Tools Hinweis */}
            {result.content_tools_integration?.tools_used?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-orange-100">
                <div className="flex items-center gap-2 text-orange-600">
                  <Lightbulb size={16} />
                  <span className="text-sm font-medium">Content-Tools verwendet:</span>
                </div>
                <div className="flex flex-wrap gap-2 mt-1">
                  {result.content_tools_integration.batch_hook_used && (
                    <span className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded-full">
                      ⚡ Hook-Generator
                    </span>
                  )}
                  {result.content_tools_integration.pain_point_topics_used && (
                    <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
                      🎯 Pain-Point-Ideen
                    </span>
                  )}
                  {result.content_tools_integration.auda_enabled && (
                    <>
                      {result.content_tools_integration.tools_used.includes('TikTok-Reels') && (
                        <span className="text-xs px-2 py-1 bg-pink-100 text-pink-700 rounded-full">
                          🎬 TikTok-Reels
                        </span>
                      )}
                      {result.content_tools_integration.tools_used.includes('Google Reviews Booster') && (
                        <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full">
                          ⭐ Google Reviews
                        </span>
                      )}
                      {result.content_tools_integration.tools_used.includes('LongCat Video') && (
                        <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
                          🎥 KI-Videos
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
            {/* Auda-Stats Anzeige */}
            {result.auda_stats && (
              <div className="mt-3 pt-3 border-t border-pink-100">
                <div className="flex items-center gap-2 text-pink-600 mb-2">
                  <Music size={16} />
                  <span className="text-sm font-medium">Auda-Style Features:</span>
                </div>
                <div className="grid grid-cols-3 gap-3 text-xs">
                  {result.auda_stats.tiktok_enabled && (
                    <div className="bg-pink-50 p-2 rounded text-center">
                      <div className="font-bold text-pink-700">{result.auda_stats.posts_with_tiktok}</div>
                      <div className="text-pink-600">TikTok-Reels</div>
                    </div>
                  )}
                  {result.auda_stats.review_links_added && (
                    <div className="bg-yellow-50 p-2 rounded text-center">
                      <div className="font-bold text-yellow-700">✓</div>
                      <div className="text-yellow-600">Review-Links</div>
                    </div>
                  )}
                  {result.auda_stats.posts_with_video > 0 && (
                    <div className="bg-purple-50 p-2 rounded text-center">
                      <div className="font-bold text-purple-700">{result.auda_stats.video_ratio}</div>
                      <div className="text-purple-600">Videos</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Batch Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 animate-fade-in max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-fox-fire rounded-lg flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="font-heading text-xl font-bold">Batch Content Generator</h2>
                  <p className="text-sm text-stone-500">Konfiguriere deine Content-Woche</p>
                </div>
              </div>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-stone-100 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-6">
              {/* NEU: Datenbasis-Integration Box */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
                <div className="flex items-center gap-2 mb-3">
                  <Database size={18} className="text-purple-600" />
                  <h3 className="font-semibold text-stone-900">Datenbasis-Integration</h3>
                  <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded font-medium">CHRISTIANI.AI</span>
                </div>
                
                <p className="text-xs text-stone-600 mb-3">
                  Nutze dein Unternehmensprofil (USPs, Tonalität, Keywords) aus der Datenbasis für optimierte Posts.
                </p>
                
                {!datenbasisProfile ? (
                  <button
                    onClick={loadDatenbasisProfile}
                    disabled={datenbasisLoading}
                    data-testid="load-datenbasis-btn"
                    className="w-full px-4 py-2 bg-purple-500 text-white rounded-lg text-sm hover:bg-purple-600 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {datenbasisLoading ? (
                      <>
                        <Loader2 className="animate-spin" size={16} />
                        Lade Profil...
                      </>
                    ) : (
                      <>
                        <Database size={16} />
                        Datenbasis-Profil laden
                      </>
                    )}
                  </button>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle size={16} className="text-green-500" />
                        <span className="text-sm font-medium text-green-700">Profil geladen</span>
                      </div>
                      <button 
                        onClick={() => { setDatenbasisProfile(null); setUseDatenbasis(false); }}
                        className="text-xs text-stone-500 hover:text-stone-700"
                      >
                        Entfernen
                      </button>
                    </div>
                    
                    {/* Toggle für Datenbasis-Nutzung */}
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={useDatenbasis}
                        onChange={(e) => setUseDatenbasis(e.target.checked)}
                        className="w-4 h-4 text-purple-600 rounded"
                      />
                      <span className="text-sm text-stone-700">Datenbasis für Posts nutzen</span>
                    </label>
                    
                    {/* Profil-Preview */}
                    <div className="p-2 bg-white rounded border border-purple-100 text-xs">
                      <div className="flex flex-wrap gap-1 mb-1">
                        <span className="text-purple-600 font-medium">Tonalität:</span>
                        <span>{datenbasisProfile.tonalitaet || 'professionell'}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mb-1">
                        <span className="text-purple-600 font-medium">USPs:</span>
                        {datenbasisProfile.usps?.slice(0, 2).map((usp, i) => (
                          <span key={i} className="px-1 py-0.5 bg-purple-50 rounded text-purple-700">{usp.substring(0, 30)}...</span>
                        ))}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        <span className="text-purple-600 font-medium">Keywords:</span>
                        {datenbasisProfile.keywords_jobcenter?.slice(0, 4).map((kw, i) => (
                          <span key={i} className="px-1 py-0.5 bg-blue-50 rounded text-blue-700">{kw}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* NEU: Content-Tools Integration Box */}
              <div className="bg-gradient-to-r from-orange-50 to-purple-50 rounded-lg p-4 border border-orange-200">
                <div className="flex items-center gap-2 mb-3">
                  <Lightbulb size={18} className="text-orange-600" />
                  <h3 className="font-semibold text-stone-900">Content-Tools Integration</h3>
                  <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded">NEU</span>
                </div>
                
                {/* Zielgruppen-Auswahl */}
                <div className="mb-3">
                  <label className="block text-sm font-medium text-stone-700 mb-1">Zielgruppe (für Auto-Hook)</label>
                  <select
                    value={targetAudience}
                    onChange={(e) => setTargetAudience(e.target.value)}
                    className="w-full p-2 border border-stone-200 rounded-lg text-sm"
                  >
                    <option value="">-- Optional auswählen --</option>
                    {audiences.map(a => (
                      <option key={a.key} value={a.key}>{a.name}</option>
                    ))}
                  </select>
                </div>
                
                {/* Auto-Hook Generator */}
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={handleGenerateHook}
                    disabled={hookLoading || !targetAudience}
                    className="flex-1 px-3 py-2 bg-orange-500 text-white rounded-lg text-sm hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {hookLoading ? <Loader2 className="animate-spin" size={14} /> : <Zap size={14} />}
                    Auto-Hook generieren
                  </button>
                  <button
                    onClick={handleLoadPainPointTopics}
                    disabled={painPointLoading || !targetAudience}
                    className="flex-1 px-3 py-2 bg-purple-500 text-white rounded-lg text-sm hover:bg-purple-600 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {painPointLoading ? <Loader2 className="animate-spin" size={14} /> : <TrendingUp size={14} />}
                    Pain-Point-Ideen laden
                  </button>
                </div>
                
                {/* Generierter Batch-Hook */}
                {batchHook && (
                  <div className="p-3 bg-white rounded-lg border border-orange-200">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-orange-600">⚡ Batch-Hook (editierbar)</span>
                      <button 
                        onClick={() => setBatchHook('')}
                        className="text-stone-400 hover:text-stone-600"
                      >
                        <X size={14} />
                      </button>
                    </div>
                    <textarea
                      value={batchHook}
                      onChange={(e) => setBatchHook(e.target.value)}
                      className="w-full p-2 text-sm border border-stone-200 rounded resize-none"
                      rows={2}
                    />
                    <p className="text-xs text-stone-500 mt-1">
                      Dieser Hook wird als Einstieg für alle Posts dieses Batches verwendet.
                    </p>
                  </div>
                )}
                
                {/* Pain-Point-Ideen geladen */}
                {painPointIdeas.length > 0 && !showProFeature && (
                  <div className="mt-2 p-3 bg-white rounded-lg border border-purple-200">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-medium text-purple-600">🎯 {painPointIdeas.length} Content-Ideen geladen</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {painPointIdeas.slice(0, 3).map((idea, i) => (
                        <span key={i} className="text-xs px-2 py-1 bg-purple-50 text-purple-700 rounded">
                          {idea.pain_point.substring(0, 30)}...
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* PRO-FEATURE: Fertige Posts aus Pain-Points */}
              <div className="border border-dashed border-purple-300 rounded-lg p-4 bg-purple-50/30">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <FileText size={18} className="text-purple-600" />
                    <h3 className="font-semibold text-stone-900">Fertige Posts aus Pain-Points</h3>
                    <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded font-medium">PRO</span>
                  </div>
                  <span className="text-xs text-stone-500">Optional</span>
                </div>
                <p className="text-xs text-stone-600 mb-3">
                  Generiere vollständige Post-Entwürfe mit Hook, Body und CTA – direkt aus der Pain-Point-Analyse. 
                  <span className="font-medium text-purple-600"> Alle Posts sind editierbar.</span>
                </p>
                
                {!showProFeature ? (
                  <button
                    onClick={handleGenerateFullPosts}
                    disabled={draftLoading || !targetAudience}
                    className="w-full px-4 py-2 border-2 border-purple-500 text-purple-600 rounded-lg text-sm hover:bg-purple-50 disabled:opacity-50 flex items-center justify-center gap-2 transition-all"
                  >
                    {draftLoading ? (
                      <>
                        <Loader2 className="animate-spin" size={16} />
                        Generiere Post-Entwürfe...
                      </>
                    ) : (
                      <>
                        <Sparkles size={16} />
                        Fertige Posts aus Pain-Point-Ideen erzeugen
                      </>
                    )}
                  </button>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-purple-600">
                        🎯 {draftPosts.length} Post-Entwürfe bereit
                      </span>
                      <button
                        onClick={() => { setShowProFeature(false); setDraftPosts([]); }}
                        className="text-xs text-stone-500 hover:text-stone-700"
                      >
                        Zurücksetzen
                      </button>
                    </div>
                    
                    {/* Draft Posts Liste */}
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {draftPosts.map((draft, idx) => (
                        <div key={idx} className="bg-white rounded-lg border border-purple-200 p-3">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">
                                🎯 Pain-Point
                              </span>
                              <span className="text-xs text-stone-500">
                                {draft.channel}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setEditingDraft(editingDraft === idx ? null : idx)}
                                className="p-1 text-stone-400 hover:text-purple-600"
                                title="Bearbeiten"
                              >
                                <Edit2 size={14} />
                              </button>
                              <button
                                onClick={() => removeDraft(idx)}
                                className="p-1 text-stone-400 hover:text-red-600"
                                title="Entfernen"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          </div>
                          
                          {editingDraft === idx ? (
                            // Editier-Modus
                            <div className="space-y-2">
                              <div>
                                <label className="text-xs font-medium text-stone-600">Hook</label>
                                <input
                                  type="text"
                                  value={draft.hook}
                                  onChange={(e) => updateDraft(idx, 'hook', e.target.value)}
                                  className="w-full p-2 text-sm border border-stone-200 rounded"
                                />
                              </div>
                              <div>
                                <label className="text-xs font-medium text-stone-600">Body</label>
                                <textarea
                                  value={draft.body}
                                  onChange={(e) => updateDraft(idx, 'body', e.target.value)}
                                  className="w-full p-2 text-sm border border-stone-200 rounded resize-none"
                                  rows={4}
                                />
                              </div>
                              <div>
                                <label className="text-xs font-medium text-stone-600">CTA</label>
                                <input
                                  type="text"
                                  value={draft.cta}
                                  onChange={(e) => updateDraft(idx, 'cta', e.target.value)}
                                  className="w-full p-2 text-sm border border-stone-200 rounded"
                                />
                              </div>
                              <button
                                onClick={() => setEditingDraft(null)}
                                className="text-xs px-2 py-1 bg-purple-500 text-white rounded"
                              >
                                Fertig
                              </button>
                            </div>
                          ) : (
                            // Vorschau-Modus
                            <div className="text-sm">
                              <p className="text-orange-600 font-medium mb-1">{draft.hook}</p>
                              <p className="text-stone-600 text-xs line-clamp-2 mb-1">{draft.body.substring(0, 100)}...</p>
                              <p className="text-purple-600 text-xs">{draft.cta}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    <p className="text-xs text-stone-500 italic">
                      Diese Entwürfe werden zusätzlich zu den KI-generierten Posts eingeplant.
                    </p>
                  </div>
                )}
              </div>
              
              {/* NEU: Auda-Style Features Box */}
              <div className="bg-gradient-to-r from-pink-50 to-red-50 rounded-lg p-4 border border-pink-200">
                <div className="flex items-center gap-2 mb-3">
                  <Video size={18} className="text-pink-600" />
                  <h3 className="font-semibold text-stone-900">Auda-Style Features</h3>
                  <span className="text-xs px-2 py-0.5 bg-pink-100 text-pink-700 rounded font-medium">POWER</span>
                </div>
                
                <div className="space-y-3">
                  {/* TikTok Checkbox — ausgeblendet solange das TikTok-Video-Produkt in Prüfung ist */}
                  {TIKTOK_VIDEO_ENABLED && (
                  <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-white/50 transition-colors">
                    <input
                      data-testid="auda-tiktok-checkbox"
                      type="checkbox"
                      checked={includeTikTok}
                      onChange={(e) => {
                        setIncludeTikTok(e.target.checked);
                        if (e.target.checked && !selectedPlatforms.includes('tiktok')) {
                          setSelectedPlatforms([...selectedPlatforms, 'tiktok']);
                        }
                      }}
                      className="w-4 h-4 text-pink-600 rounded"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-stone-900">TikTok-Reels</span>
                      <p className="text-xs text-stone-500">30-60s Videos • Post-Zeit: 17-19 Uhr DE</p>
                    </div>
                  </label>
                  )}

                  {/* Google Reviews Checkbox */}
                  <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-white/50 transition-colors">
                    <input
                      data-testid="auda-reviews-checkbox"
                      type="checkbox"
                      checked={includeReviewLinks}
                      onChange={(e) => setIncludeReviewLinks(e.target.checked)}
                      className="w-4 h-4 text-pink-600 rounded"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-stone-900">Google Reviews Booster</span>
                      <p className="text-xs text-stone-500">⭐ "Bewerte uns bei Google!" Link in jedem Post</p>
                    </div>
                  </label>
                  
                  {/* Video-Integration entfernt: Videos sind kein Kundenprodukt.
                      Der State bleibt (Default false), damit die Anfrage unverändert
                      bleibt — sie fordert nur keine Videos mehr an. */}
                </div>
                
                {(includeTikTok || includeReviewLinks || includeVideo20Percent) && (
                  <div className="mt-3 p-2 bg-white/70 rounded text-xs text-stone-600">
                    <span className="font-medium">Aktiv:</span>
                    {includeTikTok && <span className="ml-2 px-1.5 py-0.5 bg-pink-100 text-pink-700 rounded">TikTok</span>}
                    {includeReviewLinks && <span className="ml-2 px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded">Reviews</span>}
                    {includeVideo20Percent && <span className="ml-2 px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">Videos</span>}
                  </div>
                )}
              </div>
              
              {/* Days & Posts per Day */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Anzahl Tage</label>
                  <select
                    data-testid="batch-days-select"
                    value={days}
                    onChange={(e) => setDays(Number(e.target.value))}
                    className="w-full p-3 border border-stone-200 rounded-lg"
                  >
                    {[3, 5, 7, 14, 30].map(d => (
                      <option key={d} value={d}>{d} Tage</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Posts pro Tag</label>
                  <select
                    data-testid="batch-posts-select"
                    value={postsPerDay}
                    onChange={(e) => setPostsPerDay(Number(e.target.value))}
                    className="w-full p-3 border border-stone-200 rounded-lg"
                  >
                    {[1, 2, 3, 4, 5].map(p => (
                      <option key={p} value={p}>{p} Post{p > 1 ? 's' : ''} pro Plattform</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Platforms */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Plattformen</label>
                <div className="flex flex-wrap gap-2">
                  {allPlatforms.map(p => (
                    <button
                      key={p}
                      onClick={() => togglePlatform(p)}
                      className={`px-4 py-2 rounded-full text-sm transition-all flex items-center gap-2 ${
                        selectedPlatforms.includes(p)
                          ? 'bg-fox-fire text-white'
                          : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                      }`}
                    >
                      <PlatformIcon platform={p} size={14} />
                      {p.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </div>

              {/* Topics */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-stone-700">Themen</label>
                  {usePainPointTopics && (
                    <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full flex items-center gap-1">
                      <TrendingUp size={10} /> Aus Pain-Point-Analyse
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {allTopics.map(t => (
                    <button
                      key={t}
                      onClick={() => toggleTopic(t)}
                      className={`px-4 py-2 rounded-full text-sm transition-all ${
                        selectedTopics.includes(t)
                          ? 'bg-sage-wisdom text-white'
                          : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                  {/* Zusätzliche Pain-Point-Themen */}
                  {selectedTopics.filter(t => !allTopics.includes(t)).map(t => (
                    <button
                      key={t}
                      onClick={() => toggleTopic(t)}
                      className="px-4 py-2 rounded-full text-sm bg-purple-500 text-white flex items-center gap-1"
                    >
                      🎯 {t.length > 30 ? t.substring(0, 30) + '...' : t}
                      <X size={12} />
                    </button>
                  ))}
                </div>
              </div>

              {/* Tone */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Tonalität</label>
                <div className="flex flex-wrap gap-2">
                  {tones.map(t => (
                    <button
                      key={t}
                      onClick={() => setTone(t)}
                      className={`px-4 py-2 rounded-full text-sm transition-all ${
                        tone === t
                          ? 'bg-fox-fire text-white'
                          : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Preview */}
              <div className="bg-stone-50 rounded-lg p-4">
                <h3 className="font-semibold text-stone-900 mb-3">Vorschau</h3>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-3xl font-bold text-fox-fire">{totalPosts}</div>
                    <div className="text-sm text-stone-500">Posts gesamt</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-sage-wisdom">{days}</div>
                    <div className="text-sm text-stone-500">Tage</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-amber-500">~{estimatedTime}</div>
                    <div className="text-sm text-stone-500">Minuten</div>
                  </div>
                </div>
                <p className="text-xs text-stone-500 mt-3 text-center">
                  {postsPerDay} Post(s) × {selectedPlatforms.length} Plattform(en) × {days} Tage = {totalPosts} Posts
                </p>
                {/* Content-Tools Status */}
                {(batchHook || usePainPointTopics) && (
                  <div className="mt-3 pt-3 border-t border-stone-200 text-center">
                    <p className="text-xs text-orange-600 font-medium">
                      ✨ Content-Tools aktiv: 
                      {batchHook && ' Auto-Hook'}
                      {batchHook && usePainPointTopics && ' + '}
                      {usePainPointTopics && ' Pain-Point-Ideen'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowModal(false)} className="flex-1 btn-secondary">
                Abbrechen
              </button>
              <button
                data-testid="start-batch-btn"
                onClick={handleGenerate}
                disabled={loading || selectedPlatforms.length === 0}
                className="flex-1 btn-primary flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generiere {totalPosts} Posts...
                  </>
                ) : (
                  <>
                    <Sparkles size={18} />
                    {totalPosts} Posts generieren
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ContentGenerator;
